/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import store from "store";
import videojs from "video.js";

import { getVideoJsComponent } from "../utils";
import { MultiAngleSwitcherContainer } from "./MultiAngleSwitcherContainer";

import type { THEOPlayerRuntime } from "../theoplayerRuntime";
import type Player from "video.js/dist/types/player";

type MultiAngleSwitcherOptions = {
  handleSwitchMultiAngleVideo: (videoId: string) => void;
} & {
  className?: string;
};

type getVideoJsMultiAngleSwitcherComponentArgs =
  | { playerType: "THEOPlayer"; runtime: THEOPlayerRuntime }
  | { playerType: "IVS" };

export const getVideoJsMultiAngleSwitcherComponent = (
  args: getVideoJsMultiAngleSwitcherComponentArgs
) => {
  const VideoJSComponent =
    args.playerType === "IVS"
      ? videojs.getComponent("Component")
      : getVideoJsComponent("Component", args.runtime);

  /**
   * videojsでReactコンポーネントを表示するためのラッパーコンポーネント
   * @see https://videojs.org/guides/react/
   */
  class MultiAngleSwitcher extends VideoJSComponent {
    private reactRoot: ReturnType<typeof createRoot> | null = null;

    constructor(player: Player, options: MultiAngleSwitcherOptions) {
      super(player, options);
      // Bind the current class context to the mountReactComponent method
      this.mountReactComponent = this.mountReactComponent.bind(this);
      // When player is ready, call method to mount the React component
      player.ready(() => this.mountReactComponent());
    }

    mountReactComponent() {
      const options = this.options(undefined) as MultiAngleSwitcherOptions;

      if (!options.handleSwitchMultiAngleVideo) {
        return;
      }

      this.reactRoot = createRoot(this.el());
      this.reactRoot.render(
        <Provider store={store}>
          <MultiAngleSwitcherContainer
            handleSwitchMultiAngleVideo={options.handleSwitchMultiAngleVideo}
            player={this.player()}
          />
        </Provider>
      );
    }

    createEl() {
      const el = videojs.dom.createEl("div") as HTMLElement;
      return el;
    }
    dispose(options?: { originalEl: Element }): void {
      this.reactRoot?.unmount();
      this.reactRoot = null;
      super.dispose(options);
    }
  }

  return MultiAngleSwitcher;
};
